/**
 * Erste Anmeldung mit einem verschickten Passwort.
 *
 * Das Passwort kam per Mail und steht damit in mindestens zwei
 * Postfächern. Solange es gilt, lässt der Server nichts anderes zu als
 * diese eine Seite — der Zwang steckt in der Berechtigungsprüfung, nicht
 * hier. Diese Seite ist die freundliche Seite davon: sie erklärt, warum,
 * statt den Benutzer gegen eine Fehlermeldung laufen zu lassen.
 */

import { useState } from 'react'
import { api } from '../api/klient'
import { Fehlerhinweis } from '../bausteine/Hinweise'
import wortmarke from '../assets/spuer-wortmarke.png'
import type { PortalKonto } from './Portal'

export function ErstesPasswort({
  konto,
  fertig,
  abmelden,
}: {
  konto: PortalKonto
  fertig: () => void | Promise<void>
  abmelden: () => void
}) {
  const [altes, setzeAltes] = useState('')
  const [neues, setzeNeues] = useState('')
  const [wiederholung, setzeWiederholung] = useState('')
  const [fehler, setzeFehler] = useState<unknown>(null)
  const [laeuft, setzeLaeuft] = useState(false)

  const stimmtUeberein = neues === wiederholung
  const langGenug = neues.length >= 12

  async function speichern(ereignis: React.FormEvent) {
    ereignis.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await api.senden('/api/portal/passwort', {
        altes_passwort: altes,
        neues_passwort: neues,
      })
      await fertig()
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b-2 border-slate-200 bg-white">
        <div aria-hidden className="h-[3px] bg-akzent-500" />
        <div className="mx-auto flex max-w-4xl items-center gap-3 p-4">
          <img src={wortmarke} alt="SPÜR" className="h-8 w-auto shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900">Kundenportal</div>
            <div className="truncate text-base text-slate-700">{konto.kunde_name}</div>
          </div>
          <button type="button" className="knopf-still px-3" onClick={abmelden}>
            Abmelden
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-lg flex-1 p-4">
        <h1 className="ueberschrift-akzent mb-3 text-2xl">Bitte vergeben Sie ein Passwort</h1>

        <p className="mb-4 text-slate-800">
          Ihr bisheriges Passwort haben wir Ihnen per E-Mail geschickt. Es gilt nur
          für diese erste Anmeldung — eine Mail liegt danach in mehreren
          Postfächern, und ein Passwort daraus sollte nicht dauerhaft gelten.
        </p>

        <form onSubmit={(e) => void speichern(e)} className="karte space-y-3">
          <div>
            <label htmlFor="altes">Passwort aus der E-Mail</label>
            <input
              id="altes"
              type="password"
              value={altes}
              onChange={(e) => setzeAltes(e.target.value)}
              required
              autoComplete="current-password"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="neues">Ihr neues Passwort</label>
            <input
              id="neues"
              type="password"
              value={neues}
              onChange={(e) => setzeNeues(e.target.value)}
              required
              minLength={12}
              autoComplete="new-password"
            />
            <p className="mt-1 text-base text-slate-600">
              Mindestens zwölf Zeichen. Ein Satz, den nur Sie kennen, ist besser
              als ein kurzes Kunstwort.
            </p>
          </div>

          <div>
            <label htmlFor="wdh">Noch einmal zur Sicherheit</label>
            <input
              id="wdh"
              type="password"
              value={wiederholung}
              onChange={(e) => setzeWiederholung(e.target.value)}
              required
              autoComplete="new-password"
              className={
                wiederholung && !stimmtUeberein ? 'border-stufe-3 bg-stufe-3-grund' : undefined
              }
            />
            {wiederholung && !stimmtUeberein && (
              <p className="mt-1 text-base font-semibold text-stufe-3">
                Die beiden Eingaben sind nicht gleich.
              </p>
            )}
          </div>

          <Fehlerhinweis fehler={fehler} />

          <button
            type="submit"
            className="knopf-haupt w-full"
            disabled={laeuft || !stimmtUeberein || !langGenug}
          >
            {laeuft ? 'Speichert …' : 'Passwort setzen und fortfahren'}
          </button>
        </form>
      </main>
    </div>
  )
}
